'use client';
import React, { useEffect, useRef, useState } from 'react';

interface DeductionRow {
  expense: string;
  weekly: string;
  isTotal?: boolean;
}

const deductions: DeductionRow[] = [
  { expense: 'Cargo & Liability Insurance', weekly: '$300/week' },
  { expense: 'ELD Support', weekly: '$65/week' },
  { expense: 'IFTA', weekly: '$15/week' },
  { expense: 'Total Weekly Deductions', weekly: '$380/week', isTotal: true },
];

interface CompensationCard {
  program: string;
  rate: string;
  detail: string;
  highlight: boolean;
}

const compensationCards: CompensationCard[] = [
  {
    program: 'Amazon Relay Contracts',
    rate: '90% of Gross Revenue',
    detail: '10% dispatch fee. No escrow. No hidden fees. Average weekly gross $8,000–$11,000.',
    highlight: true,
  },
  {
    program: 'Street & Spot Loads',
    rate: '12% Dispatch Fee',
    detail: 'Rate and revenue depend on load, lane, and market conditions. More flexibility, more home time.',
    highlight: false,
  },
];

interface IncludedItem {
  label: string;
  detail: string;
  icon: string;
}

const includedItems: IncludedItem[] = [
  { icon: '⛽', label: 'Fuel Card', detail: 'We provide a fuel card to help manage fuel costs on the road.' },
  { icon: '📋', label: 'Permits & IFTA', detail: 'We handle permits and IFTA so you can focus on driving.' },
  { icon: '📦', label: 'Setup Package Mailed', detail: 'IFTA, company decals, fuel card, and ELD device (if needed) shipped to your home address.' },
  { icon: '🏠', label: 'Start From Home', detail: 'Drivers start from their home location. We email the setup package to your address.' },
  { icon: '🪪', label: 'Plate Program', detail: '$1,850 total over 6 weeks at ~$308/week deduction during the payment period.' },
  { icon: '🚛', label: 'Power Only', detail: 'Drop & Hook operation. Amazon provides trailers on contract freight — no company trailers needed.' },
];

const PayPerMileTable: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);
  const revealRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setAnimated(true);
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="pay-table" className="py-32 relative bg-plum-deep/40" ref={sectionRef}>
      <div className="beam-border-h" style={{ top: 0, bottom: 'auto' }} />
      <div className="dot-pattern absolute inset-0 opacity-15 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div
          className="reveal mb-16 grid md:grid-cols-12 gap-8"
          ref={(el) => { revealRefs.current[0] = el; }}>
          <div className="md:col-span-1">
            <span className="section-num">02/</span>
          </div>
          <div className="md:col-span-6 space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-violet">
              Compensation & Deductions
            </p>
            <h2 className="text-display font-bold tracking-tight leading-tight text-cream">
              Transparent Pay Structure.
              <span className="text-silver-lavender/50"> No Hidden Fees.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-base text-silver-lavender/60 leading-relaxed font-light">
              Our goal is to provide clear information about the compensation structure
              before you begin operating. No escrow. No surprises.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left column: compensation + deductions */}
          <div
            className="lg:col-span-7 space-y-8 reveal"
            ref={(el) => { revealRefs.current[1] = el; }}>

            {/* Compensation cards */}
            <div className="space-y-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/40">Pay Structure by Program</p>
              {compensationCards.map((c) => (
                <div
                  key={c.program}
                  className={`border rounded-2xl p-6 ${c.highlight ? 'border-violet/40 bg-violet/8' : 'border-silver-lavender/15 bg-plum-card'}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-widest text-silver-lavender/50 mb-1">{c.program}</p>
                      <p className={`text-2xl font-bold ${c.highlight ? 'text-violet' : 'text-cream'}`}>{c.rate}</p>
                    </div>
                    {c.highlight && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-violet bg-violet/15 border border-violet/25 px-3 py-1.5 rounded-full shrink-0">
                        Best Value
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-silver-lavender/55 font-light leading-relaxed">{c.detail}</p>
                </div>
              ))}
            </div>

            {/* Weekly deductions table */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-4">Weekly Fixed Deductions (Amazon Program)</p>
              <div className="border border-silver-lavender/15 rounded-2xl overflow-hidden">
                <div className="bg-plum-deep/80 px-6 py-3 grid grid-cols-2 border-b border-silver-lavender/10">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40">Expense</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 text-right">Weekly Cost</div>
                </div>
                {deductions.map((d) => (
                  <div
                    key={d.expense}
                    className={`grid grid-cols-2 px-6 py-4 border-b border-silver-lavender/8 ${d.isTotal ? 'bg-violet/8 border-violet/20' : 'table-row-hover'}`}>
                    <span className={`text-sm font-medium ${d.isTotal ? 'text-cream font-bold' : 'text-silver-lavender/70'}`}>
                      {d.expense}
                    </span>
                    <span className={`text-sm font-bold text-right ${d.isTotal ? 'text-violet' : 'text-cream'}`}>
                      {d.weekly}
                    </span>
                  </div>
                ))}
                <div className="bg-plum-deep/40 px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-silver-lavender/30">
                  No Escrow · No Hidden Fees · Clearly explained before onboarding
                </div>
              </div>
            </div>

            {/* Plate program */}
            <div className="bg-plum-card border border-silver-lavender/15 rounded-2xl p-6">
              <p className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-4">Plate Program (If You Need a Plate)</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-cream">$1,850</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mt-1">Total Cost</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-cream">6</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mt-1">Weeks</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-violet">~$308</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-silver-lavender/40 mt-1">Per Week</p>
                </div>
              </div>
              <p className="text-xs text-silver-lavender/40 mt-4 font-light">
                The plate deduction applies during the six-week payment period only.
              </p>
            </div>
          </div>

          {/* Right column: what's included */}
          <div
            className="lg:col-span-5 reveal reveal-delay-2"
            ref={(el) => { revealRefs.current[2] = el; }}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-silver-lavender/40 mb-4">What We Provide</p>
            <div className="space-y-4">
              {includedItems.map((item) => (
                <div
                  key={item.label}
                  className="flex gap-4 bg-plum-card border border-silver-lavender/10 rounded-xl p-4 hover:border-violet/20 transition-colors">
                  <span className="text-2xl shrink-0">{item.icon}</span>
                  <div>
                    <p className="text-sm font-bold text-cream mb-1">{item.label}</p>
                    <p className="text-sm text-silver-lavender/55 font-light leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pull-quote */}
            <div
              className="mt-6 bg-plum-card border-l-2 border-violet rounded-r-2xl p-6 reveal"
              ref={(el) => { revealRefs.current[3] = el; }}>
              <p className="text-base text-silver-lavender/80 italic leading-relaxed font-light">
                &ldquo;We understand that every Owner-Operator has different priorities. Instead of
                forcing every driver into the same operating model, we offer different freight
                options so you can choose the approach that works best for your business.&rdquo;
              </p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet mt-3">
                — Velocity Trucking LLC
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PayPerMileTable;